import { Box, TextField, Typography } from "@mui/material";
import { Trans } from "@lingui/react/macro";
import InputGrayStart from "@/components/core/input-gray-start";
import React, { useEffect, useMemo } from "react";
import { useProductStore } from "@/components/context/product-store";
import { useShallow } from "zustand/shallow";
import { IOfferzBuyOptions } from "@/types/product";
import { magicConstants } from "@/utils/magic-constants";

interface ProductOptionsProps {
  buyOptions: IOfferzBuyOptions[];
}
const ProductOptions = ({ buyOptions }: ProductOptionsProps) => {
  const { inputValues, setInputValues, apiErrors, availableOptions, setAvailableOptions } =
    useProductStore(
      useShallow((state) => ({
        inputValues: state.inputValues,
        setInputValues: state.setInputValues,
        apiErrors: state.apiErrors,
        availableOptions: state.availableOptions,
        setAvailableOptions: state.setAvailableOptions,
      })),
    );

  const filteredOptions = useMemo(() => {
    return buyOptions.filter(
      (option: IOfferzBuyOptions) =>
        option.required === 1 && option.option_id !== magicConstants.priceOptionID,
    );
  }, [buyOptions]);

  useEffect(() => {
    setAvailableOptions(filteredOptions);
  }, [filteredOptions, setAvailableOptions]);

  if (availableOptions.length === 0) return null;
  return (
    <Box>
      <Typography variant="h5" sx={{ mt: 3 }}>
        <Trans>Available Options</Trans>
      </Typography>

      {availableOptions.map((option: IOfferzBuyOptions) => (
        <Box key={option.name} sx={{ mt: 1 }}>
          <Typography variant="subtitle1" sx={{ display: "flex" }}>
            <Trans>{option.name}</Trans>
            {option.required === 1 && (
              <Typography component="span" sx={{ color: "red" }}>
                *
              </Typography>
            )}
          </Typography>

          {/* Special input for serial number */}
          {option.option_id === magicConstants.serialNumberOptionID ? (
            <InputGrayStart
              placeholder="12345678"
              name="serial"
              type="text"
              value={inputValues[option.product_option_id] || ""}
              onChange={(e) => setInputValues(option.product_option_id, e.target.value)}
              startAdornmentLabel="SN"
              error={!!apiErrors?.[option.product_option_id]}
              helperText={apiErrors?.[option.product_option_id] || ""}
            />
          ) : (
            <TextField
              data-testid={option.product_option_id}
              type={option.type}
              placeholder={option.name}
              name={option.name}
              value={inputValues[option.product_option_id] || ""}
              onChange={(e) => setInputValues(option.product_option_id, e.target.value)}
              sx={{
                pt: "4px",
                "& .MuiOutlinedInput-root": {
                  padding: 0,
                },
                "& .MuiFormHelperText-root.Mui-error": {
                  color: "#fd7b7c",
                },
              }}
              error={!!apiErrors?.[option.product_option_id]}
              helperText={apiErrors?.[option.product_option_id] || ""}
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default React.memo(ProductOptions);
